import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import getSymbolFromCurrency from "currency-symbol-map";

import { useAppSelector, useAppDispatch } from "store/index";
import { handleUpdateFields } from "store/segments/slice";
import { apiRoutes } from "configs/apiRoutes";
import instance from "api/apiClient";

import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import InnerInput from "components/inputs/InnerInput/InnerInput";
import SwitchBtn from "components/buttons/SwitchBtn";
import FilterTabs from "components/FilterTabs";
import ModalTabSkeleton from "components/global/Skeleton/ModalTabSkeleton/ModalTabSkeleton";

interface Props {
  fieldErrors?: Record<string, string>;
  clearFieldError: (field: string) => void;
  currency: string;
  setCurrency: (e: string) => void;
  onLoadingChange: (isLoading: boolean) => void;
}

const SegmentRulesPaymentsTab: React.FC<Props> = ({
  currency,
  setCurrency,
  fieldErrors,
  clearFieldError,
  onLoadingChange,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const currenciesList = useAppSelector((store) => store.settings.currencies);
  const [options, setOptions] = useState<{ value: number; label: string }[]>(
    []
  );
  const [isProvidersLoading, setIsProvidersLoading] = useState(true);

  const optionsTypes = [
    {
      value: "card",
      label: t("card"),
    },
    {
      value: "eWallet",
      label: t("eWallet"),
    },
    {
      value: "bankTransfer",
      label: t("bankTransfer"),
    },
    {
      value: "crypto",
      label: t("crypto"),
    },
    {
      value: "mobilePayment",
      label: t("mobilePayment"),
    },
    {
      value: "voucher",
      label: t("voucher"),
    },
    {
      value: "cash",
      label: t("cash"),
    },
  ];

  const itemPayload = useAppSelector(
    (store) =>
      store.segments.payload?.paymentRules?.filter(
        (el) => el.currency === currency
      )[0]
  );
  const payloadFull = useAppSelector((store) => store.segments.payload);

  const handleProviders = async () => {
    setIsProvidersLoading(true);
    try {
      const result = await instance.get(
        `${apiRoutes.paymentProvidersList}?limit=500&offset=0`
      );
      const array = result.data.items.map((el: any) => {
        return {
          value: el.id,
          label: el.title,
        };
      });
      setOptions(array);
    } catch (error) {
      console.log(error, "error");
    } finally {
      setIsProvidersLoading(false);
    }
  };
  useEffect(() => {
    handleProviders();
  }, []);

  useEffect(() => {
    onLoadingChange(isProvidersLoading);
  }, [isProvidersLoading, onLoadingChange]);

  const handleData = (
    key: string,
    value: string | boolean | number | number[] | string[]
  ) => {
    clearFieldError(key);
    const existingList = payloadFull?.paymentRules || [];

    const existingRule = existingList.find(
      (el) => el.currency === currency
    ) || { currency };

    const updatedRule = {
      ...existingRule,
      [key]:
        key === "paymentProviders" || key === "paymentTypes"
          ? value
          : parseFloat(value as string),
    };

    const updatedList = [
      ...existingList.filter((el) => el.currency !== currency),
      updatedRule,
    ];
    //   @ts-ignore
    dispatch(handleUpdateFields({ paymentRules: updatedList }));
  };

  if (isProvidersLoading) return <ModalTabSkeleton />;

  return (
    <div className="rules-payments-wrapper">
      <div className="game-modal-tabs small">
        {currenciesList.map((el) => (
          <div
            className={`tab-item ${currency === el ? "active" : ""}`}
            key={el}
            onClick={() => {
              setCurrency(el);
            }}
          >
            <div className="currency-tab-item">
              <div className="currency">{getSymbolFromCurrency(el)}</div> {el}
            </div>
          </div>
        ))}
      </div>
      <div className="segment-rules-form-wrapper">
        <div className="segments-rules-form-box">
          <div className="segments-rules-form-box-item">
            <SwitchBtn
              isActive={
                !!itemPayload?.depositAmountMin ||
                !!itemPayload?.depositAmountMax
              }
              handleClick={() => {
                const isDisabled =
                  (itemPayload?.depositAmountMin === 0 ||
                    itemPayload?.depositAmountMin === undefined) &&
                  (itemPayload?.depositAmountMax === 0 ||
                    itemPayload?.depositAmountMax === undefined);
                dispatch(
                  // @ts-ignore
                  handleUpdateFields({
                    paymentRules: payloadFull?.paymentRules
                      ? [
                          ...payloadFull.paymentRules.filter(
                            (el) => el.currency !== currency
                          ),
                          {
                            ...itemPayload,
                            depositAmountMin: isDisabled ? 1 : 0,
                            depositAmountMax: isDisabled ? 1 : 0,
                          },
                        ]
                      : [
                          {
                            ...itemPayload,
                            depositAmountMin: isDisabled ? 1 : 0,
                            depositAmountMax: isDisabled ? 1 : 0,
                          },
                        ],
                  })
                );
              }}
            />
            <InnerInput
              hasError={Boolean(fieldErrors?.["depositAmountMin"])}
              value={itemPayload?.depositAmountMin?.toString() ?? ""}
              placeholder={t("Min")}
              label={t("Min")}
              type="number"
              id="segment-depositAmountMin"
              onChange={(e) => handleData("depositAmountMin", e)}
              disabled={itemPayload?.depositAmountMin === 0}
            />
            <span className="txt">{t("depositAmount")}</span>
            <InnerInput
              hasError={Boolean(fieldErrors?.["depositAmountMax"])}
              value={itemPayload?.depositAmountMax?.toString() ?? ""}
              placeholder={t("Max")}
              label={t("Max")}
              type="number"
              id="segment-depositAmountMax"
              onChange={(e) => handleData("depositAmountMax", e)}
              disabled={itemPayload?.depositAmountMax === 0}
            />
          </div>
          <div className="segments-rules-form-box-item">
            <SwitchBtn
              isActive={
                !!itemPayload?.ftdAmountMin || !!itemPayload?.ftdAmountMax
              }
              handleClick={() => {
                const isDisabled =
                  (itemPayload?.ftdAmountMin === 0 ||
                    itemPayload?.ftdAmountMin === undefined) &&
                  (itemPayload?.ftdAmountMax === 0 ||
                    itemPayload?.ftdAmountMax === undefined);
                dispatch(
                  // @ts-ignore
                  handleUpdateFields({
                    paymentRules: payloadFull?.paymentRules
                      ? [
                          ...payloadFull.paymentRules.filter(
                            (el) => el.currency !== currency
                          ),
                          {
                            ...itemPayload,
                            ftdAmountMin: isDisabled ? 1 : 0,
                            ftdAmountMax: isDisabled ? 1 : 0,
                          },
                        ]
                      : [
                          {
                            ...itemPayload,
                            ftdAmountMin: isDisabled ? 1 : 0,
                            ftdAmountMax: isDisabled ? 1 : 0,
                          },
                        ],
                  })
                );
              }}
            />
            <InnerInput
              hasError={Boolean(fieldErrors?.["ftdAmountMin"])}
              value={itemPayload?.ftdAmountMin?.toString() ?? ""}
              placeholder={t("Min")}
              label={t("Min")}
              type="number"
              id="segment-ftdAmountMin"
              onChange={(e) => handleData("ftdAmountMin", e)}
              disabled={itemPayload?.ftdAmountMin === 0}
            />
            <span className="txt">{t("ftdAmount")}</span>
            <InnerInput
              hasError={Boolean(fieldErrors?.["ftdAmountMax"])}
              value={itemPayload?.ftdAmountMax?.toString() ?? ""}
              placeholder={t("Max")}
              label={t("Max")}
              type="number"
              id="segment-ftdAmountMax"
              onChange={(e) => handleData("ftdAmountMax", e)}
              disabled={itemPayload?.ftdAmountMax === 0}
            />
          </div>
          <div className="segments-rules-form-box-item">
            <SwitchBtn
              isActive={
                !!itemPayload?.depositCountMin || !!itemPayload?.depositCountMax
              }
              handleClick={() => {
                const isDisabled =
                  (itemPayload?.depositCountMin === 0 ||
                    itemPayload?.depositCountMin === undefined) &&
                  (itemPayload?.depositCountMax === 0 ||
                    itemPayload?.depositCountMax === undefined);
                dispatch(
                  // @ts-ignore
                  handleUpdateFields({
                    paymentRules: payloadFull?.paymentRules
                      ? [
                          ...payloadFull.paymentRules.filter(
                            (el) => el.currency !== currency
                          ),
                          {
                            ...itemPayload,
                            depositCountMin: isDisabled ? 1 : 0,
                            depositCountMax: isDisabled ? 1 : 0,
                          },
                        ]
                      : [
                          {
                            ...itemPayload,
                            depositCountMin: isDisabled ? 1 : 0,
                            depositCountMax: isDisabled ? 1 : 0,
                          },
                        ],
                  })
                );
              }}
            />
            <InnerInput
              hasError={Boolean(fieldErrors?.["depositCountMin"])}
              value={itemPayload?.depositCountMin?.toString() ?? ""}
              placeholder={t("Min")}
              label={t("Min")}
              type="number"
              id="segment-depositCountMin"
              onChange={(e) => handleData("depositCountMin", e)}
              disabled={itemPayload?.depositCountMin === 0}
            />
            <span className="txt">{t("depositCount")}</span>
            <InnerInput
              hasError={Boolean(fieldErrors?.["depositCountMax"])}
              value={itemPayload?.depositCountMax?.toString() ?? ""}
              placeholder={t("Max")}
              label={t("Max")}
              type="number"
              id="segment-depositCountMax"
              onChange={(e) => handleData("depositCountMax", e)}
              disabled={itemPayload?.depositCountMax === 0}
            />
          </div>
          <div className="segments-rules-form-box-item">
            <SwitchBtn
              isActive={
                !!itemPayload?.depositSumMin || !!itemPayload?.depositSumMax
              }
              handleClick={() => {
                const isDisabled =
                  (itemPayload?.depositSumMin === 0 ||
                    itemPayload?.depositSumMin === undefined) &&
                  (itemPayload?.depositSumMax === 0 ||
                    itemPayload?.depositSumMax === undefined);
                dispatch(
                  // @ts-ignore
                  handleUpdateFields({
                    paymentRules: payloadFull?.paymentRules
                      ? [
                          ...payloadFull.paymentRules.filter(
                            (el) => el.currency !== currency
                          ),
                          {
                            ...itemPayload,
                            depositSumMin: isDisabled ? 1 : 0,
                            depositSumMax: isDisabled ? 1 : 0,
                          },
                        ]
                      : [
                          {
                            ...itemPayload,
                            depositSumMin: isDisabled ? 1 : 0,
                            depositSumMax: isDisabled ? 1 : 0,
                          },
                        ],
                  })
                );
              }}
            />
            <InnerInput
              hasError={Boolean(fieldErrors?.["depositSumMin"])}
              value={itemPayload?.depositSumMin?.toString() ?? ""}
              placeholder={t("Min")}
              label={t("Min")}
              type="number"
              id="segment-depositSumMin"
              onChange={(e) => handleData("depositSumMin", e)}
              disabled={itemPayload?.depositSumMin === 0}
            />
            <span className="txt">{t("depositSum")}</span>
            <InnerInput
              hasError={Boolean(fieldErrors?.["depositSumMax"])}
              value={itemPayload?.depositSumMax?.toString() ?? ""}
              placeholder={t("Max")}
              label={t("Max")}
              type="number"
              id="segment-depositSumMax"
              onChange={(e) => handleData("depositSumMax", e)}
              disabled={itemPayload?.depositSumMax === 0}
            />
          </div>
          <div className="segments-rules-form-box-item">
            <SwitchBtn
              isActive={
                !!itemPayload?.depositAvgMin || !!itemPayload?.depositAvgMax
              }
              handleClick={() => {
                const isDisabled =
                  (itemPayload?.depositAvgMin === 0 ||
                    itemPayload?.depositAvgMin === undefined) &&
                  (itemPayload?.depositAvgMax === 0 ||
                    itemPayload?.depositAvgMax === undefined);
                dispatch(
                  // @ts-ignore
                  handleUpdateFields({
                    paymentRules: payloadFull?.paymentRules
                      ? [
                          ...payloadFull.paymentRules.filter(
                            (el) => el.currency !== currency
                          ),
                          {
                            ...itemPayload,
                            depositAvgMin: isDisabled ? 1 : 0,
                            depositAvgMax: isDisabled ? 1 : 0,
                          },
                        ]
                      : [
                          {
                            ...itemPayload,
                            depositAvgMin: isDisabled ? 1 : 0,
                            depositAvgMax: isDisabled ? 1 : 0,
                          },
                        ],
                  })
                );
              }}
            />
            <InnerInput
              hasError={Boolean(fieldErrors?.["depositAvgMin"])}
              value={itemPayload?.depositAvgMin?.toString() ?? ""}
              placeholder={t("Min")}
              label={t("Min")}
              type="number"
              id="segment-depositAvgMin"
              onChange={(e) => handleData("depositAvgMin", e)}
              disabled={itemPayload?.depositAvgMin === 0}
            />
            <span className="txt">{t("depositAvg")}</span>
            <InnerInput
              hasError={Boolean(fieldErrors?.["depositAvgMax"])}
              value={itemPayload?.depositAvgMax?.toString() ?? ""}
              placeholder={t("Max")}
              label={t("Max")}
              type="number"
              id="segment-depositAvgMax"
              onChange={(e) => handleData("depositAvgMax", e)}
              disabled={itemPayload?.depositAvgMax === 0}
            />
          </div>
        </div>
        <div className="segments-rules-form-box">
          <div className="segments-rules-multiselect">
            <DefaultDropdown
              onSelect={(e) => {
                handleData(
                  "paymentProviders",
                  itemPayload?.paymentProviders
                    ? [...itemPayload?.paymentProviders, e as number]
                    : [e as number]
                );
              }}
              placeholder={t("paymentProvider")}
              label={t("paymentProvider")}
              options={options.filter(
                (el) => !itemPayload?.paymentProviders?.includes(el.value)
              )}
              disabled={options.length <= 0}
            />
            <FilterTabs
              onSelect={(e) => {
                handleData(
                  "paymentProviders",
                  // @ts-ignore
                  itemPayload?.paymentProviders?.filter((el) => el !== e)
                );
              }}
              options={options.filter((el) =>
                itemPayload?.paymentProviders?.includes(el.value)
              )}
            />
          </div>
          <div className="segments-rules-multiselect">
            <DefaultDropdown
              onSelect={(e) => {
                handleData(
                  "paymentTypes",
                  itemPayload?.paymentTypes
                    ? [...itemPayload?.paymentTypes, e as string]
                    : [e as string]
                );
              }}
              placeholder={t("paymentTypes")}
              label={t("paymentTypes")}
              options={optionsTypes.filter(
                (el) => !itemPayload?.paymentTypes?.includes(el.value)
              )}
              disabled={optionsTypes.length <= 0}
            />
            <FilterTabs
              onSelect={(e) => {
                handleData(
                  "paymentTypes",
                  // @ts-ignore
                  itemPayload?.paymentTypes?.filter((el) => el !== e)
                );
              }}
              options={optionsTypes.filter((el) =>
                itemPayload?.paymentTypes?.includes(el.value)
              )}
            />
          </div>
          <div className="segments-rules-switcher">
            <SwitchBtn
              activeTxt={t("active")}
              isActive={itemPayload?.isActive === 1}
              handleClick={() =>
                handleData(
                  "isActive",
                  itemPayload?.isActive && itemPayload?.isActive === 1 ? 0 : 1
                )
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SegmentRulesPaymentsTab;
